import * as THREE from 'three';
import { detectQuality } from '../core/Engine.js';

/**
 * The light rig: cascaded sun shadows, sky fill, and a fixed-size dynamic light
 * pool for muzzle flashes and explosions.
 *
 * ## Cascaded shadow maps
 *
 * three.js has no CSM in core. This implements it with N DirectionalLights that
 * share a direction, each with an orthographic shadow camera fitted to a slice
 * of view depth. The catch is that three would then add all N lights together
 * and triple-light the scene, so `lights_fragment_begin` is patched (once, at
 * module load, before any material compiles) to weight each directional light by
 * a cascade selection function of `vViewPosition.z`. The weights sum to exactly
 * 1.0 everywhere and cross-fade across the split boundaries, which is what stops
 * cascade transitions from popping.
 *
 * Patching the ShaderChunk rather than individual materials is deliberate: it
 * covers every lit material in the game, including ones other systems create
 * later, with zero bookkeeping and zero risk of one material being missed and
 * rendering 3x overbright.
 *
 * The splits are compiled in as literals because they never change at runtime,
 * which means the injection needs no uniforms at all.
 *
 * ## Dynamic lights
 *
 * three.js recompiles every shader in the scene when the light *count* changes.
 * Adding a PointLight for a muzzle flash would therefore cost a few hundred
 * milliseconds — on every shot. So the pool is allocated at full size up front
 * and only intensity/position/colour are ever touched.
 */

export const TUNING = {
  /** Direct sun. Tuned against ACES @ exposure 1.0 with ~0.4 albedo surfaces.
   *  Deliberately much larger than the fill: the lit/shadow ratio *is* the look. */
  sunIntensity: 7.6,
  /** Pulls the (very orange) physical sun colour back toward white. 0 = raw physics.
   *  A 13-degree sun transmits at roughly (1.0, 0.50, 0.11) — true, but unusable. */
  sunDesaturate: 0.42,

  /** Safety net for pooled lights created with `life <= 0` (caller-managed).
   *  Long enough for any legitimate manual light, short enough that a dropped
   *  handle cannot light the map for the rest of the match. */
  maxManualLife: 8,

  /** Sky-dome fill. Keeps shadows cool blue-grey rather than dead black.
   *  Every point of this you add is a point of contrast you lose.
   *
   *  Lowered from 0.5: `scene.environment` is a PMREM of the same sky, so skylight
   *  was being counted twice on every surface — once as IBL, once here. Doubling
   *  the bluest term in the scene is what kept shadowed asphalt navy even after
   *  the IBL was elevation-ramped and this light was desaturated. */
  hemiIntensity: 0.3,
  /** Zenith blue at golden hour is near-monochromatic; this keeps the fill from
   *  turning shadows cartoon-blue.
   *
   *  Raised from 0.2 after the elevation-ramped IBL fix: the env map and this
   *  light are two independent paths to the same up-facing normals, and only the
   *  env map was ramped. A HemisphereLight blends sky->ground by normal.y, so a
   *  ground plane receives the sky colour at *full* strength — which is exactly
   *  where the residual navy in shadowed asphalt was coming from. */
  hemiDesaturate: 0.46,
  /** Warm bounce floor. Larger than it looks like it should be, on purpose:
   *  an up-facing surface sees mostly the *upper* sky, which at golden hour is
   *  the bluest part of the dome, so unopposed IBL turns every road navy. This
   *  is the counterweight, and it costs no specular quality to raise.
   *
   *  Most of it has moved into `bounceIntensity` below. What was here was a
   *  *white AmbientLight*, which adds the same value to every normal on a
   *  surface — it cannot be modulated by anything, so it does not merely
   *  flatten normal maps in shade, it mathematically erases them. Together with
   *  a hemisphere light (which varies only with normal.y) the entire fill had
   *  no horizontal direction at all, which is why a brick wall showed its
   *  mortar on the lit face and flat maroon on the shaded one.
   *
   *  Only trimmed, not gutted, and the difference matters: this is the one warm
   *  term an *up-facing* normal receives, and the bounce below deliberately
   *  gives such normals nothing. Cutting it hard would have handed shaded
   *  asphalt back to the hemisphere light and the sky IBL — both cool — and
   *  re-created the navy road this value was raised to fix in the first place.
   *  The goal here is directional variation in shade, not less light in it. */
  ambientIntensity: 0.38,

  /** Ground/wall bounce, as a directional light. Bounce is the term the sky IBL
   *  omits and it is strongly directional: a face turned away from the sun is
   *  lit mostly by the sunlit ground *in front of* it, which is why this sits on
   *  the anti-solar azimuth and below the horizon. It is what puts a gradient —
   *  and therefore normal-map detail — back on shaded faces.
   *
   *  Higher than the ambient it replaces because a directional contributes
   *  `I * max(0, N dot L)` rather than `I` flat, so it needs roughly twice the
   *  number to land at the same level on the normals it does face. */
  bounceIntensity: 0.5,
  /** Degrees away from the sun's azimuth. 180 = directly anti-solar. */
  bounceAzimuth: 180,
  /** Degrees above the horizon; negative means it comes up off the ground, which
   *  is where bounce actually comes from. Up-facing surfaces get nothing from
   *  it — they already have the sky. */
  bounceElevation: -12,

  /** How far back along the sun ray the shadow cameras sit. A low sun needs a lot
   *  of room: at 13 degrees a 30 m tower is 133 m away along the light ray. */
  shadowBackDistance: 178,
  /** Slack added to each cascade radius so the blend band is fully covered. */
  cascadePad: 1.09,
  /** Depth bias in world metres, before conversion to the shadow camera's range.
   *  Stays small on purpose: with the sun at 13 degrees, depth bias slides a
   *  ground shadow by `bias / sin(13 deg)` — 4.4x — so it is the most expensive
   *  possible way to buy acne resistance. The receiver-plane bias in the filter
   *  does that job instead; this is only a numerical floor. */
  biasMetres: 0.008,
  /** Normal-offset bias as a multiple of the cascade's world texel size.
   *
   *  Was 1.45, the textbook figure for a wide box PCF, and it is what peeled
   *  contact shadows off enemies' feet: it worked out to 0.085 m on cascade 1
   *  and 0.208 m on cascade 2, and a *vertical* offset of `n` displaces a ground
   *  shadow by `n / sin(13 deg)`, so cascade 2 was detaching contact by nearly a
   *  metre. The filter no longer needs it — it biases along the receiver's own
   *  plane instead — so this is now a small safety net for polygon edges, and
   *  additionally capped in absolute metres below. */
  normalBiasTexels: 0.35,
  /** Hard ceiling on the normal offset regardless of texel size. This is the
   *  number that governs how far a contact shadow can ever detach. */
  normalBiasMax: 0.018,
  /** Target penumbra width in metres.
   *
   *  Measured, do not raise: the "dashed horizontal stripes on the ground" that
   *  the player reported are NOT softenable by this. They are the real shadows
   *  of the market-stall roof rails and other thin ground-level props, raked by
   *  a 13 degree sun — which multiplies every caster's height by 1/tan(13) =
   *  4.33 on its way to the floor, so a 5 cm rail 4 m up lands as a ~20 cm band
   *  and a rack of them lands as a regular dashed grille metres across. Taking
   *  this to 0.16 m (cascade 0 kernel 1.17 -> 3.6 texels, 7.7 cm penumbra) was
   *  photographed against the same frame and changed nothing visible, because
   *  softening a 20 cm band's edges by 8 cm still leaves a 20 cm band. It only
   *  cost contact sharpness. See the handover notes for what does move it.
   *
   *  The sun's angular diameter is 0.53 deg, so
   *  a physically exact penumbra is ~0.9% of the caster-to-receiver distance —
   *  millimetres for the contact cases that were smearing. That is far below one
   *  texel in any cascade, so the honest target is "as tight as the cascade's
   *  resolution allows", and this only softens cascade 0, which has resolution
   *  to spare, enough to antialias rather than stairstep. */
  penumbraMetres: 0.05,
  /** Kernel spacing bounds in texels. The lower bound keeps a sub-texel kernel
   *  from collapsing to an unfiltered lookup and aliasing. */
  penumbraTexelRange: [0.6, 1.6],
  /** Contrast applied to the filtered shadow value. A tent filter ramps across
   *  the whole kernel; this pulls the visible transition back toward the width a
   *  0.53-degree sun would actually produce, while leaving enough intermediate
   *  levels to avoid a stairstepped edge. 1.0 disables it. */
  edgeSharpen: 1.7,

  /** Hard cap on simultaneous dynamic lights. */
  maxDynamicLights: 8,
  /** Default falloff radius for pooled lights. */
  defaultLightDistance: 9,

  /** Viewmodel scene key light, as a fraction of the world sun. */
  /**
   * Viewmodel key/fill/rim, as fractions of the world sun and hemisphere.
   *
   * Key pulled back from 0.85. `gunmetal` was tuned to `envIntensity 1.7` during
   * a window when the CSM weighting bug was silently zeroing every viewmodel light
   * past index 0 — so the rim was rendering pure black and the key was doing all
   * the work. Once the rim came back, the sum blew the rail tops to flat white.
   * Two fixes landing in opposite directions on the same surface; this rebalances
   * for the corrected total rather than undoing either.
   */
  viewKeyScale: 0.62,
  viewFillScale: 0.55,
  /**
   * Rim on the viewmodel. A first-person weapon is nearly always framed against
   * sky or a bright wall, and with key + fill alone its silhouette dissolves into
   * the background. Kept deliberately weak and behind the gun so it separates the
   * edge without acting as a second key and flattening the form.
   */
  viewRimScale: 0.30,
};

/* ============================ cascade shader patch ========================== */

const CASCADE_SPLITS = {
  1: [95],
  2: [26, 120],
  3: [20, 55, 135],
};
/** Fraction of a split used as the cross-fade band into the next cascade. */
const CASCADE_BLEND = 0.18;

const DIR_DECL = 'DirectionalLight directionalLight;';
const DIR_INFO = 'getDirectionalLightInfo( directionalLight, directLight );';
const DIR_SHADOW_CALL =
  'directLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( directionalShadowMap[ i ], directionalLightShadow.shadowMapSize, directionalLightShadow.shadowIntensity, directionalLightShadow.shadowBias, directionalLightShadow.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;';
const SHADOW_FN_ANCHOR = 'float getShadow( sampler2D shadowMap,';

/**
 * Replacement PCF for the sun cascades. Three things are wrong with the one
 * three.js provides, and all three are visible from across the map:
 *
 * 1. **`PCFSoftShadowMap` ignores `shadow.radius` entirely.** Its kernel is a
 *    fixed 4-texel-wide bilinear blur, so the penumbra width is whatever the
 *    cascade's texel size happens to be, times four. Measured on cascade 1
 *    (58.6 mm texels) that is a 23 cm smear on every edge, which is why a
 *    canopy 30 cm off a wall — physically a ~3 mm penumbra — arrived as a
 *    hand's width of grey, and why a 5 cm scaffold pipe read as a soft band
 *    rather than a pipe. Here the kernel spacing is `shadowRadius` texels, so
 *    the parameter is live and the penumbra is a decision instead of an
 *    accident.
 *
 * 2. **Acne is fought with position offsets, which is what causes
 *    peter-panning.** `normalBias` shifts the shadow lookup along the surface
 *    normal; with the sun at 13 degrees a vertical offset of `n` slides a
 *    ground shadow by `n / sin(13 deg)` — a factor of 4.4. Cascade 2's 0.208 m
 *    normal bias was therefore detaching contact shadows from enemies' feet by
 *    the better part of a metre. So instead of offsetting anything, each tap
 *    compares against the depth the *receiver's own plane* would have at that
 *    tap, obtained from screen-space derivatives of the shadow coordinate
 *    (receiver-plane depth bias). A flat surface is then acne-free at any
 *    incidence with no offset at all, which lets `normalBiasTexels` drop to a
 *    fraction of a texel and puts contact shadows back on the ground.
 *
 * 3. A box/bilinear filter spreads a step edge over the full kernel. The tent
 *    weighting plus the contrast curve at the end keep one texel of
 *    antialiasing while pulling the visible transition back in, which is what
 *    a 0.53-degree sun should look like.
 *
 * The gradient clamp matters: at a silhouette the derivatives are taken across
 * a depth discontinuity and predict nonsense, so the per-tap correction is
 * limited to what a very grazing plane could legitimately need, scaled by the
 * cascade's own depth units (which is what `shadowBias` carries).
 */
function shadowFilterGLSL(sharpen) {
  const f = (n) => n.toFixed(4);
  return /* glsl */ `
	vec2 obReceiverPlane( vec3 coord ) {
		vec3 ddx = dFdx( coord );
		vec3 ddy = dFdy( coord );
		float det = ddx.x * ddy.y - ddx.y * ddy.x;
		if ( abs( det ) < 1e-12 ) return vec2( 0.0 );
		return vec2(
			ddy.y * ddx.z - ddx.y * ddy.z,
			ddx.x * ddy.z - ddy.x * ddx.z
		) / det;
	}

	float obDirectionalShadow( sampler2D shadowMap, vec2 shadowMapSize, float shadowIntensity, float shadowBias, float shadowRadius, vec4 shadowCoord ) {
		vec3 coord = shadowCoord.xyz / shadowCoord.w;

		// Derivatives first, before any branch. They read neighbouring fragments
		// in the quad, so taking them under divergent control flow — such as the
		// out-of-frustum early-out that used to live here — is undefined, and
		// the shadow map border is exactly where quads diverge.
		vec2 grad = obReceiverPlane( coord );

		bool inside = coord.z <= 1.0
			&& coord.x >= 0.0 && coord.x <= 1.0
			&& coord.y >= 0.0 && coord.y <= 1.0;

		float sum = 0.0;
		if ( inside ) {
			vec2 spacing = shadowRadius / shadowMapSize;
			float maxDz = abs( shadowBias ) * shadowRadius * 8.0;
			float z = coord.z + shadowBias;

			for ( int y = -1; y <= 1; y ++ ) {
				for ( int x = -1; x <= 1; x ++ ) {
					vec2 o = vec2( float( x ), float( y ) ) * spacing;
					float w = ( 2.0 - abs( float( x ) ) ) * ( 2.0 - abs( float( y ) ) );
					float dz = clamp( dot( o, grad ), - maxDz, maxDz );
					sum += w * texture2DCompare( shadowMap, coord.xy + o, z + dz );
				}
			}
			sum *= 1.0 / 16.0;
			sum = clamp( ( sum - 0.5 ) * ${f(sharpen)} + 0.5, 0.0, 1.0 );
		} else {
			sum = 1.0;
		}

		return mix( 1.0, sum, shadowIntensity );
	}

	`;
}

/** Splices the filter above into the shadow chunk. Idempotent under HMR. */
function installShadowFilter(sharpen) {
  const store = THREE.ShaderChunk;
  if (!store.__obPristineShadowPars) store.__obPristineShadowPars = store.shadowmap_pars_fragment;
  const pristine = store.__obPristineShadowPars;

  if (!pristine.includes(SHADOW_FN_ANCHOR)) {
    console.warn('[lighting] three.js shadow chunk changed shape — custom PCF disabled');
    store.shadowmap_pars_fragment = pristine;
    return false;
  }
  // Inserted just above getShadow(), which places it after texture2DCompare()
  // and inside the `#ifdef USE_SHADOWMAP` guard the whole chunk lives in.
  store.shadowmap_pars_fragment = pristine.replace(
    SHADOW_FN_ANCHOR,
    `${shadowFilterGLSL(sharpen)}${SHADOW_FN_ANCHOR}`,
  );
  return true;
}

/**
 * Builds the GLSL that computes per-cascade weights and splices it into
 * `lights_fragment_begin`. Idempotent — safe under Vite HMR.
 */
function installCascadeChunk(count, splits, useCustomFilter = true) {
  const store = THREE.ShaderChunk;
  if (!store.__obPristineLights) store.__obPristineLights = store.lights_fragment_begin;
  const pristine = store.__obPristineLights;

  if (!pristine.includes(DIR_DECL) || !pristine.includes(DIR_INFO) || !pristine.includes(DIR_SHADOW_CALL)) {
    console.warn('[lighting] three.js lighting chunk changed shape — CSM disabled');
    store.lights_fragment_begin = pristine;
    return false;
  }

  // Route the sun through the replacement filter whether or not cascades are on.
  const call = useCustomFilter
    ? DIR_SHADOW_CALL.replace('getShadow(', 'obDirectionalShadow(')
    : DIR_SHADOW_CALL;

  if (count <= 1) {
    store.lights_fragment_begin = pristine.replace(DIR_SHADOW_CALL, call);
    return false;
  }

  const f = (n) => n.toFixed(4);
  // Radial distance, not `vViewPosition.z`.
  //
  // Each cascade is a *sphere* of radius `split * cascadePad` centred on the
  // player (see _fitCascade), so the only selection metric that agrees with it
  // is distance from the player. Selecting on axial depth does not: with an 80
  // degree vertical FOV at 16:9 the frame corner is 59.7 degrees off-axis, so a
  // fragment at axial depth d sits at radial distance d / cos(59.7) = 1.98 d.
  // Cascade 0 (split 20 m, sphere radius 21.8 m) therefore stops covering the
  // screen corners at 11 m of axial depth, while the axial test kept handing
  // them to cascade 0 out to 16.4 m — where the lookup falls outside the map,
  // returns "lit", and the shadow simply disappears toward the frame edges.
  //
  // Radial selection costs one `length()` and nothing else: at the screen
  // centre radial == axial, so cascade 0 still owns the first 20 m dead ahead
  // at exactly its current resolution. Only off-axis fragments — the ones
  // cascade 0 never covered in the first place — move outward a cascade early.
  let decl = `\tfloat csmW[ ${count} ];\n\t{\n\t\tfloat csmDepth = length( vViewPosition );\n`;
  for (let i = 0; i < count - 1; i++) {
    const end = splits[i];
    const start = end * (1 - CASCADE_BLEND);
    decl += `\t\tfloat csmT${i} = smoothstep( ${f(start)}, ${f(end)}, csmDepth );\n`;
  }
  decl += `\t\tcsmW[ 0 ] = 1.0 - csmT0;\n`;
  for (let i = 1; i < count - 1; i++) {
    decl += `\t\tcsmW[ ${i} ] = csmT${i - 1} * ( 1.0 - csmT${i} );\n`;
  }
  decl += `\t\tcsmW[ ${count - 1} ] = csmT${count - 2};\n\t}\n`;

  // `USE_SHADOWMAP` is the only compile-time signal that separates the world
  // scene from the viewmodel scene, and it is a reliable one: three defines it
  // per program from `shadowMap.enabled && scene has shadow casters`, the world
  // always has the cascades, and the viewmodel rig deliberately casts nothing.
  //
  // Without this guard the weighting also ran in `viewScene`, where the lights
  // are not cascades at all — every directional light past index 0 was being
  // multiplied by a cascade weight computed from the *viewmodel's* view depth
  // of ~0.3 m, which is 0 for every cascade above the first. The rim light
  // three lines further down in this file has therefore been multiplied to
  // black since it was added. Any non-cascade directional light in the world
  // scene is covered too: three sorts shadow-casting lights first, so the
  // cascades always hold indices 0..count-1 and the index test excludes the rest.
  const guard = `#if defined( USE_SHADOWMAP )`;
  const pick =
    `\n\t\t${guard} && ( UNROLLED_LOOP_INDEX < ${count} )\n` +
    `\t\tdirectLight.color *= csmW[ UNROLLED_LOOP_INDEX ];\n` +
    `\t\t#endif`;

  // Cascades whose weight is zero have already had their contribution zeroed by
  // the line above, so their shadow lookup cannot affect the result — skipping
  // it turns 3 x 9 taps per fragment into 9 (18 inside a blend band). That is
  // where the budget for a better filter comes from.
  const guardedCall =
    `#if ( UNROLLED_LOOP_INDEX < ${count} )\n` +
    `\t\tif ( csmW[ UNROLLED_LOOP_INDEX ] > 0.0 )\n` +
    `\t\t#endif\n` +
    `\t\t${call}`;

  store.lights_fragment_begin = pristine
    .replace(DIR_DECL, `${DIR_DECL}\n${guard}\n${decl}#endif\n`)
    .replace(DIR_INFO, `${DIR_INFO}${pick}`)
    .replace(DIR_SHADOW_CALL, guardedCall);
  return true;
}

// Must happen before the first lit material compiles, and three caches programs
// by material type rather than by source — so it happens at module load, which
// is before main() constructs anything that draws.
const CASCADE_COUNT = THREE.MathUtils.clamp(detectQuality().cascades ?? 3, 1, 3);
const SPLITS = CASCADE_SPLITS[CASCADE_COUNT];
// If the filter patch ever fails to apply, the cascade weighting must still go
// in — without it every lit surface is lit `CASCADE_COUNT` times over — so the
// two are installed independently and the weighting falls back to three's PCF.
const PCF_ACTIVE = installShadowFilter(TUNING.edgeSharpen);
const CSM_ACTIVE = installCascadeChunk(CASCADE_COUNT, SPLITS, PCF_ACTIVE);

/* ================================= Lighting ================================= */

const UP = new THREE.Vector3(0, 1, 0);
const ORIGIN = new THREE.Vector3(0, 0, 0);
const _v = new THREE.Vector3();
const _center = new THREE.Vector3();

export class Lighting {
  constructor(ctx) {
    this.ctx = ctx;
    this.sky = ctx.sky ?? null;

    this.cascadeCount = CASCADE_COUNT;
    this.splits = SPLITS;
    if ((ctx.quality.cascades ?? CASCADE_COUNT) !== CASCADE_COUNT) {
      console.warn(
        `[lighting] quality.cascades=${ctx.quality.cascades} but the shader was compiled for ` +
          `${CASCADE_COUNT}; using ${CASCADE_COUNT}.`,
      );
    }

    this.sunDirection = this.sky?.sunDirection?.clone() ?? new THREE.Vector3(0.35, 0.19, 0.92).normalize();

    // Light-space basis, used to snap the shadow cameras to texel increments.
    this._basis = new THREE.Matrix4();
    this._basisInv = new THREE.Matrix4();

    this._buildCascades();
    this._buildFill();
    this._buildPool();
    this._buildViewmodelRig();

    this.onSkyChanged();

    ctx.engine.registerUpdate((dt, elapsed) => this.update(dt, elapsed), 'sim');
  }

  /* ------------------------------- construction ----------------------------- */

  _buildCascades() {
    const { scene, quality } = this.ctx;
    const base = quality.shadowMapSize ?? 2048;

    this.cascades = [];
    for (let i = 0; i < this.cascadeCount; i++) {
      const radius = this.splits[i] * TUNING.cascadePad;
      // The outermost cascade covers a huge area nobody inspects closely, so
      // below 'ultra' it drops to half resolution — one fewer full-size depth
      // pass per frame, for detail loss you cannot see at 135 m.
      const isOuter = i === this.cascadeCount - 1 && this.cascadeCount > 1;
      const mapSize = isOuter && quality.level !== 'ultra' ? Math.max(512, base / 2) : base;

      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.name = `sun_cascade_${i}`;
      light.castShadow = true;

      const s = light.shadow;
      s.mapSize.set(mapSize, mapSize);
      s.camera.left = -radius;
      s.camera.right = radius;
      s.camera.top = radius;
      s.camera.bottom = -radius;
      s.camera.near = 0.5;
      s.camera.far = TUNING.shadowBackDistance + radius * 2 + 70;
      s.camera.updateProjectionMatrix();

      const texel = (radius * 2) / mapSize;
      const depthRange = s.camera.far - s.camera.near;
      s.bias = -(TUNING.biasMetres + texel * 0.35) / depthRange;
      s.normalBias = Math.min(texel * TUNING.normalBiasTexels, TUNING.normalBiasMax);
      // `radius` is the filter kernel's spacing in texels, and unlike under
      // `PCFSoftShadowMap` — where it is silently ignored — it now does exactly
      // that. Solving it per cascade from a target width in *metres* is what
      // makes the penumbra consistent across cascade boundaries instead of
      // scaling with each cascade's texel size, which is what made the outer
      // cascades look like a different renderer from cascade 0.
      s.radius = THREE.MathUtils.clamp(
        TUNING.penumbraMetres / (2 * texel),
        TUNING.penumbraTexelRange[0],
        TUNING.penumbraTexelRange[1],
      );
      s.autoUpdate = true;

      scene.add(light);
      scene.add(light.target);

      this.cascades.push({ light, target: light.target, radius, mapSize, texel });
    }

    /** The primary sun light. Other systems can read direction/colour from it. */
    this.sun = this.cascades[0].light;
  }

  _buildFill() {
    const { scene } = this.ctx;

    // Sky fill. The environment map already supplies most of the indirect light
    // (see Sky.js); this hemisphere is the steerable part — it is what decides
    // whether shadowed concrete reads as cool blue-grey or as flat mud.
    this.hemi = new THREE.HemisphereLight(0x9db9dd, 0x4a3a2a, TUNING.hemiIntensity);
    scene.add(this.hemi);

    // Stand-in for bounce light off warm dusty ground and walls. A HemisphereLight
    // can only give up-facing normals sky colour, so without this every horizontal
    // surface in shade goes navy — real shade is lifted by warm bounce.
    this.ambient = new THREE.AmbientLight(0xffffff, TUNING.ambientIntensity);
    scene.add(this.ambient);

    // The directional half of that bounce. Added after the cascades, and it
    // casts no shadow, so three's shadow-casters-first ordering guarantees it
    // lands at an index the cascade weighting ignores. Direction is set from the
    // sun in onSkyChanged().
    this.bounce = new THREE.DirectionalLight(0xffffff, TUNING.bounceIntensity);
    this.bounce.name = 'sun_bounce';
    this.bounce.castShadow = false;
    scene.add(this.bounce);
    scene.add(this.bounce.target);
  }

  _buildPool() {
    const { scene, quality } = this.ctx;
    const cap =
      quality.level === 'medium' ? 4 : quality.level === 'high' ? 6 : TUNING.maxDynamicLights;
    this.poolSize = Math.min(cap, TUNING.maxDynamicLights);

    this.pool = [];
    for (let i = 0; i < this.poolSize; i++) {
      const light = new THREE.PointLight(0xffffff, 0, TUNING.defaultLightDistance, 2);
      light.name = `dynamic_${i}`;
      light.castShadow = false; // shadow-casting point lights are 6 extra passes each
      scene.add(light);
      this.pool.push({
        light,
        slot: i,
        serial: 0,
        active: false,
        life: 0,
        age: 0,
        baseIntensity: 0,
      });
    }
    this._serial = 1;
  }

  /**
   * The viewmodel lives in its own scene with its own camera, so it receives
   * none of the world's lights. Without a rig here the weapon renders as a
   * silhouette. Kept consistent with the world key/fill so the gun belongs.
   */
  _buildViewmodelRig() {
    const vs = this.ctx.viewScene;
    if (!vs) return;
    this.viewKey = new THREE.DirectionalLight(0xffffff, 1);
    this.viewKey.position.set(-0.6, 1.0, 0.55);
    vs.add(this.viewKey);
    vs.add(this.viewKey.target);

    this.viewFill = new THREE.HemisphereLight(0x9db9dd, 0x4a3a2a, 1);
    vs.add(this.viewFill);

    // Behind and slightly above, on the opposite side to the key, so the lit edge
    // runs down the weapon's far silhouette. Colour is driven from the sun in
    // onSkyChanged so it never reads as light from nowhere.
    this.viewRim = new THREE.DirectionalLight(0xffffff, 1);
    this.viewRim.position.set(0.35, 0.5, -1.0);
    vs.add(this.viewRim);
    vs.add(this.viewRim.target);
  }

  /* --------------------------------- sky sync -------------------------------- */

  /** Re-reads every colour from Sky. Called on construction and whenever the sun moves. */
  onSkyChanged() {
    const sky = this.ctx.sky ?? this.sky;
    if (!sky) return;
    this.sky = sky;
    this.sunDirection.copy(sky.sunDirection);

    // Raw transmittance at this sun elevation is close to blood orange; pull it back
    // so the scene reads golden rather than post-apocalyptic. The *hue* still
    // comes from the same physics the sky dome is drawn with, so they agree.
    const sun = sky.sunColor.clone().lerp(new THREE.Color(1, 1, 1), TUNING.sunDesaturate);
    const intensity = TUNING.sunIntensity;
    for (const c of this.cascades) {
      c.light.color.copy(sun);
      c.light.intensity = intensity;
    }

    // Normalise the sampled sky/ground radiance to a hue; the intensity knob
    // stays in TUNING so it can be tuned independently of sky exposure.
    const WHITE = new THREE.Color(1, 1, 1);
    this.hemi.color.copy(normalizeHue(sky.skyColor)).lerp(WHITE, TUNING.hemiDesaturate);
    this.hemi.groundColor.copy(normalizeHue(sky.groundColor)).lerp(WHITE, TUNING.hemiDesaturate * 0.5);
    this.hemi.intensity = TUNING.hemiIntensity;

    // Bounce takes its hue from the sunlit dust, not from the sky, and leans
    // most of the way to the sun colour: a sky-only IBL omits the large bounce
    // that horizontal surfaces get from sunlit walls and ground, and that
    // omission is warm light, so leaving it out biases everything cool.
    this.ambient.color.copy(normalizeHue(sky.groundColor)).lerp(sun, 0.6);
    this.ambient.intensity = TUNING.ambientIntensity;

    // Bounce sits on the anti-solar azimuth, coming up off the ground. Its
    // colour is the ground's, warmed toward the sun that is lighting that
    // ground — bounce is sunlight that has been through one diffuse reflection.
    if (this.bounce) {
      const az = Math.atan2(this.sunDirection.z, this.sunDirection.x) +
        THREE.MathUtils.degToRad(TUNING.bounceAzimuth);
      const el = THREE.MathUtils.degToRad(TUNING.bounceElevation);
      // Position is *toward* the light, matching how three reads a directional.
      this.bounce.position.set(Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el))
        .multiplyScalar(60);
      this.bounce.target.position.set(0, 0, 0);
      this.bounce.target.updateMatrixWorld();
      this.bounce.color.copy(normalizeHue(sky.groundColor)).lerp(sun, 0.45);
      this.bounce.intensity = TUNING.bounceIntensity;
    }

    this._basis.lookAt(this.sunDirection, ORIGIN, UP);
    this._basisInv.copy(this._basis).invert();

    if (this.viewKey) {
      this.viewKey.color.copy(sun);
      this.viewKey.intensity = TUNING.sunIntensity * TUNING.viewKeyScale;
      this.viewFill.color.copy(this.hemi.color);
      this.viewFill.groundColor.copy(this.hemi.groundColor);
      this.viewFill.intensity = TUNING.hemiIntensity * TUNING.viewFillScale;
      // Rim runs slightly warmer than the key — a backlit edge at golden hour is
      // lit by the sun directly, with none of the sky's cool fill mixed in.
      this.viewRim.color.copy(sun).lerp(new THREE.Color(1.0, 0.72, 0.42), 0.35);
      this.viewRim.intensity = TUNING.sunIntensity * TUNING.viewRimScale;
    }

    this._fitAll();
  }

  /* ---------------------------------- frame ---------------------------------- */

  update(dt) {
    this._fitAll();
    this._updatePool(dt);
  }

  _fitAll() {
    const player = this.ctx.player;
    const camera = this.ctx.camera;
    // Player does not exist for the first few frames of boot.
    if (player?.position) {
      _center.set(player.position.x, player.position.y + 1.2, player.position.z);
    } else if (camera) {
      _center.copy(camera.position);
    } else {
      _center.set(0, 1.2, 0);
    }

    for (const c of this.cascades) this._fitCascade(c, _center);
  }

  /**
   * Centres a cascade on the player and snaps it to whole shadow-map texels in
   * light space. Without the snap the shadow map resamples on every sub-texel
   * move and the shadow edges crawl and shimmer as you walk — extremely visible,
   * and this is the entire fix.
   */
  _fitCascade(c, center) {
    _v.copy(center).applyMatrix4(this._basisInv);
    _v.x = Math.round(_v.x / c.texel) * c.texel;
    _v.y = Math.round(_v.y / c.texel) * c.texel;
    _v.applyMatrix4(this._basis);

    c.target.position.copy(_v);
    c.light.position.copy(_v).addScaledVector(this.sunDirection, TUNING.shadowBackDistance);
    c.target.updateMatrixWorld();
    c.light.updateMatrixWorld();
  }

  _updatePool(dt) {
    for (const e of this.pool) {
      if (!e.active) continue;
      e.age += dt;

      // `life <= 0` means the caller manages release through its handle. That is a
      // legitimate mode, but a fire-and-forget caller that drops the handle used to
      // leave the light burning at full intensity forever — the pool would fill
      // with eight permanent max-intensity lights and every gunshot would light the
      // whole map. Cap it: nothing survives past `maxManualLife` without warning.
      if (e.life <= 0) {
        if (e.age > TUNING.maxManualLife) {
          if (!this._warnedLeak) {
            this._warnedLeak = true;
            console.warn(
              '[lighting] a manually-managed point light outlived %ds and was force-released. ' +
                'Pass a `life` to addPointLight() for fire-and-forget lights.',
              TUNING.maxManualLife
            );
          }
          this._deactivate(e);
        }
        continue;
      }

      const t = e.age / e.life;
      if (t >= 1) {
        this._deactivate(e);
      } else {
        // Quadratic falloff: a muzzle flash should die fast, not linearly fade.
        const k = 1 - t;
        e.light.intensity = e.baseIntensity * k * k;
      }
    }
  }

  _deactivate(e) {
    e.active = false;
    e.life = 0;
    e.age = 0;
    e.light.intensity = 0; // never remove: removing recompiles every shader
  }

  /* ----------------------------------- API ----------------------------------- */

  /**
   * Claim a dynamic light. Recycles the oldest entry when the pool is full.
   *
   * @param {THREE.Vector3} position
   * @param {THREE.ColorRepresentation} color
   * @param {number} intensity
   * @param {number} [distance] falloff radius in metres
   * @param {number} [life] seconds until it fades out and releases itself; 0 = manual
   * @returns {{ setPosition:Function, setColor:Function, setIntensity:Function,
   *             release:Function, alive:boolean, light:THREE.PointLight }}
   */
  addPointLight(position, color = 0xffffff, intensity = 8, distance = TUNING.defaultLightDistance, life = 0) {
    const entry = this._claim();
    const serial = this._serial++;
    entry.serial = serial;
    entry.active = true;
    entry.age = 0;
    entry.life = life;
    entry.baseIntensity = intensity;

    const l = entry.light;
    if (position) l.position.copy(position);
    l.color.set(color);
    l.intensity = intensity;
    l.distance = distance;

    return makeHandle(this, entry, serial);
  }

  _claim() {
    let free = null;
    let oldest = this.pool[0];
    for (const e of this.pool) {
      if (!e.active) {
        free = e;
        break;
      }
      if (e.serial < oldest.serial) oldest = e;
    }
    return free ?? oldest;
  }

  /** Current sun colour multiplied by intensity — useful for FX that want to match. */
  get sunRadiance() {
    return this.sun.color.clone().multiplyScalar(this.sun.intensity);
  }

  dispose() {
    for (const c of this.cascades) {
      c.light.shadow.map?.dispose();
      this.ctx.scene.remove(c.light, c.target);
    }
    for (const e of this.pool) this.ctx.scene.remove(e.light);
    this.ctx.scene.remove(this.hemi, this.ambient);
    if (this.bounce) this.ctx.scene.remove(this.bounce, this.bounce.target);
  }
}

/* --------------------------------- helpers --------------------------------- */

function makeHandle(rig, entry, serial) {
  const valid = () => entry.serial === serial && entry.active;
  return {
    light: entry.light,
    get alive() {
      return valid();
    },
    setPosition(p) {
      if (valid()) entry.light.position.copy(p);
    },
    setColor(c) {
      if (valid()) entry.light.color.set(c);
    },
    setIntensity(i) {
      if (valid()) {
        entry.baseIntensity = i;
        entry.light.intensity = i;
      }
    },
    setDistance(d) {
      if (valid()) entry.light.distance = d;
    },
    release() {
      if (valid()) rig._deactivate(entry);
    },
  };
}

/** Strips brightness from a sampled radiance, keeping only its hue. */
function normalizeHue(color) {
  const peak = Math.max(color.r, color.g, color.b, 1e-5);
  return new THREE.Color(color.r / peak, color.g / peak, color.b / peak);
}

export { CSM_ACTIVE, PCF_ACTIVE, CASCADE_COUNT };
